import { useEffect, useState } from "react";
import { Skeleton } from "~/components/ui/skeleton";
import { NewSessionPicker } from "./NewSessionPicker";
import { ProjectSetupWizard } from "./ProjectSetupWizard";
import { useProjectContext } from "./useProjectContext";

type Mode = "loading" | "setup" | "session";

const LoadingSkeleton = () => (
  <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
    <div className="flex w-full max-w-lg flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <div className="flex flex-col gap-1">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-10 rounded-md" />
        ))}
      </div>
    </div>
  </div>
);

export const ProjectIndex = () => {
  const { attachedAgents, attachedDatasets } = useProjectContext();
  const [mode, setMode] = useState<Mode>("loading");

  const isLoading = attachedAgents === undefined || attachedDatasets === undefined;

  // Set mode once when data arrives — prevents reactive transitions mid-setup
  // (e.g. attaching agents on the setup screen should not skip to the picker).
  // A project needs both an agent and a dataset to start a session, so setup
  // runs whenever either is missing; the session splash assumes both exist.
  useEffect(() => {
    if (mode !== "loading" || isLoading) {
      return;
    }
    setMode(attachedAgents.length === 0 || attachedDatasets.length === 0 ? "setup" : "session");
  }, [mode, isLoading, attachedAgents, attachedDatasets]);

  if (mode === "loading") {
    return <LoadingSkeleton />;
  }

  if (mode === "setup") {
    return <ProjectSetupWizard onContinue={() => setMode("session")} />;
  }

  return <NewSessionPicker />;
};
